import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell
} from 'recharts';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import EditVendorProfileModal from '../components/EditVendorProfileModal';

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────
const fmt = (n) => (n || 0).toLocaleString('en-IN');

function getDateRange(preset) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;
  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      start = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      start = new Date(2020, 0, 1);
  }
  return { start, end };
}

function filterByDate(records, dateField, start, end) {
  if (!Array.isArray(records)) return [];
  return records.filter(r => {
    const d = new Date(r[dateField]);
    return d >= start && d <= end;
  });
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'Last 7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'year',  label: 'This Year' },
  { key: 'all',   label: 'All Time' },
];

function safeStr(val) {
  return String(val ?? '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ');
}

function exportCSV(orders, vendorName, preset) {
  function fmtDateTime(v) {
    if (!v) return 'N/A';
    const d = new Date(v);
    if (isNaN(d.getTime())) return 'N/A';
    const dd   = String(d.getDate()).padStart(2, '0');
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh   = String(d.getHours()).padStart(2, '0');
    const min  = String(d.getMinutes()).padStart(2, '0');
    return `\t${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  const header = [
    'Date & Time', 'Order ID', 'Product', 'Quantity', 'Total Amount (INR)', 'Buyer Name', 'Status'
  ];

  const rows = orders.map(o => {
    const dtStr = fmtDateTime(o.createdAt);
    const orderId = safeStr(String(o._id || '').slice(-10).toUpperCase());
    const product = safeStr(o.productName || o.productId?.name || 'Product');
    const qty = o.quantity || 1;
    const total = o.totalAmount || 0;
    const buyer = safeStr(o.userId?.name || 'Customer');
    const status = safeStr(o.status || 'Pending');
    return [dtStr, orderId, product, qty, total, buyer, status];
  });

  const totalRevenue = orders.reduce((s, o) => s + (o.totalAmount || 0), 0);
  const totalUnits = orders.reduce((s, o) => s + (o.quantity || 1), 0);

  const footer = [
    [],
    ['--- SUMMARY ---'],
    ['Vendor', safeStr(vendorName)],
    ['Period', preset.toUpperCase()],
    ['Total Units Sold', totalUnits],
    ['Total Revenue (INR)', totalRevenue],
    ['Total Orders', orders.length],
    ['Generated On', new Date().toLocaleString('en-IN')],
  ];

  const allRows = [header, ...rows, ...footer];
  const csv = allRows.map(r => r.map(c => `"${safeStr(c)}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${(vendorName || 'Vendor').replace(/\s+/g, '_')}_Orders_${preset}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────
// Custom Recharts Tooltip
// ─────────────────────────────────────────
const GoldTooltip = ({ active, payload, label, isProductView }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value || 0;
  const mainText = isProductView ? `${val.toLocaleString('en-IN')} Units` : `₹${val.toLocaleString('en-IN')}`;
  const subText = isProductView 
    ? `₹${(payload[0]?.payload?.totalSales || 0).toLocaleString('en-IN')} revenue` 
    : `${payload[0]?.payload?.count || 0} units sold`;

  return (
    <div style={{
      background: 'rgba(18,5,5,0.95)',
      border: '1px solid rgba(212,175,55,0.3)',
      borderRadius: 12,
      padding: '10px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
    }}>
      <p style={{ color: '#E8B84B', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</p>
      <p style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>
        {mainText}
      </p>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, marginTop: 2 }}>
        {subText}
      </p>
    </div>
  );
};

// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const feedRef = useRef(null);

  const [summary, setSummary]       = useState(null);
  const [allOrders, setAllOrders]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [chartToggle, setChartToggle] = useState('product'); // 'product' | 'centre'
  const [preset, setPreset]         = useState('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [vendorProfile, setVendorProfile] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [summaryRes, ordersRes, profileRes] = await Promise.allSettled([
        api.get('/admin/summary'),
        api.get('/admin/orders'),
        api.get('/vendors/me'),
      ]);

      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data?.data || null);
      }
      if (ordersRes.status === 'fulfilled') {
        setAllOrders(ordersRes.value.data?.data || []);
      }
      if (profileRes.status === 'fulfilled') {
        setVendorProfile(profileRes.value.data?.data || null);
      }
    } catch (err) {
      console.error('VendorDashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // LIVE: real-time polling every 15s keeps every number a fresh aggregation
  useEffect(() => { fetchAll(); const id = setInterval(fetchAll, 15000); return () => clearInterval(id); }, [fetchAll]);

  // ── Derived metrics ──────────────────────────────────────
  const vendorName = vendorProfile?.shopName || user?.name || 'Vendor';

  // Apply Date Filter to Orders
  const { start, end } = getDateRange(preset);
  const filteredOrders = filterByDate(allOrders, 'createdAt', start, end);

  const stats = summary?.stats || {};
  const totalProducts = stats.totalProducts || 0;
  
  // Calculate stats based on filtered orders (case-insensitive status checks)
  const isCancelled = (o) => (o.status || '').toLowerCase() === 'cancelled';
  const isDelivered = (o) => (o.status || '').toLowerCase() === 'delivered';

  const totalOrders   = filteredOrders.length;
  const totalUnits    = filteredOrders.reduce((acc, o) => acc + (isCancelled(o) ? 0 : (o.quantity || 1)), 0);
  const pendingOrders = filteredOrders.filter(o => !isDelivered(o) && !isCancelled(o)).length;
  const totalRevenue  = filteredOrders.reduce((acc, o) => acc + (isCancelled(o) ? 0 : (o.totalAmount || 0)), 0);

  // ── Build activity feed (Unaffected by date filter) ──
  const orderFeedItems = allOrders.slice(0, 8).map(o => ({
    id:   o._id,
    type: 'order',
    buyer: o.userId?.name || 'A devotee',
    productName: o.productName || o.productId?.name || 'Spiritual Item',
    quantity: o.quantity || 1,
    amount: o.totalAmount || 0,
    time: o.createdAt,
    status: o.status
  }));

  const feed = orderFeedItems.sort((a, b) => new Date(b.time) - new Date(a.time));

  // ── Chart data calculated dynamically from filteredOrders ──
  const productMap = {};
  const centreMap = {};

  filteredOrders.forEach(o => {
    // Skip cancelled orders from sales charts (case-insensitive)
    if (isCancelled(o)) return;

    const prodName = o.productName || o.productId?.name || 'Unknown';
    const centreName = o.centreName || 'General';

    if (!productMap[prodName]) productMap[prodName] = { _id: prodName, count: 0, totalSales: 0 };
    productMap[prodName].count += (o.quantity || 1);
    productMap[prodName].totalSales += (o.totalAmount || 0);

    // Only add to centreMap if centreId exists (so we don't just dump everything in 'General' if no centre)
    if (o.centreId) {
      if (!centreMap[centreName]) centreMap[centreName] = { _id: centreName, count: 0, totalSales: 0 };
      centreMap[centreName].count += (o.quantity || 1);
      centreMap[centreName].totalSales += (o.totalAmount || 0);
    }
  });

  const salesByProduct = Object.values(productMap).sort((a, b) => b.count - a.count).slice(0, 10);
  const salesByCentre = Object.values(centreMap).sort((a, b) => b.totalSales - a.totalSales).slice(0, 10);

  const activeChartData = chartToggle === 'product' ? salesByProduct : salesByCentre;
  // If vendor doesn't have multiple centres or data, hide centre toggle option
  const showCentreToggle = (summary?.salesByCentre?.length || 0) > 0 || salesByCentre.length > 0;

  // ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-[#E8B84B]/20 border-t-[#E8B84B] animate-spin mx-auto mb-6" />
          <p className="text-[#E8B84B] font-serif text-lg tracking-widest">LOADING PORTAL...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ background: 'linear-gradient(160deg, rgba(18,11,46,.5) 0%, rgba(12,7,36,.28) 40%, rgba(6,3,17,0) 100%)' }}>

      {/* ══════════════════════════════════════════
          A. HERO GREETING
      ══════════════════════════════════════════ */}
      <div className="relative overflow-hidden px-8 pt-10 pb-8">
        {/* Background mandala glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(212,175,55,0.06) 0%, transparent 70%)'
        }} />
        {/* Decorative Store symbol */}
        <div className="absolute right-8 top-4 text-[120px] select-none pointer-events-none material-symbols-outlined"
          style={{ color: 'rgba(212,175,55,0.05)', lineHeight: 1 }}>storefront</div>

        <div className="relative z-10 flex items-start justify-between flex-wrap gap-6">
          <div>
            {/* Greeting */}
            <p className="text-[#E8B84B]/70 text-xs font-bold uppercase tracking-[0.35em] mb-3">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1 className="font-serif text-3xl font-bold leading-tight mb-2" style={{ color: '#FDF6E3' }}>
              Namaste, {vendorName}!
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="material-symbols-outlined text-[16px]" style={{ color: '#E8B84B' }}>storefront</span>
              <p style={{ color: 'rgba(212,175,55,0.8)', fontSize: 13, fontWeight: 600 }}>
                Vendor Partner Dashboard & Revenue
              </p>
            </div>
            <button 
              onClick={() => setIsEditModalOpen(true)}
              className="mt-4 px-4 py-1.5 rounded bg-[#E8B84B]/10 text-[#E8B84B] border border-[#E8B84B]/30 text-xs font-bold hover:bg-[#E8B84B]/20 transition-colors flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[14px]">edit</span> Edit Profile Details
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Presets */}
            <div className="flex rounded-xl overflow-hidden shadow-lg" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.key)}
                  className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all"
                  style={{
                    background: preset === p.key ? 'rgba(212,175,55,0.2)' : 'rgba(0,0,0,0.3)',
                    color: preset === p.key ? '#E8B84B' : 'rgba(212,175,55,0.4)',
                    borderRight: p.key !== 'all' ? '1px solid rgba(212,175,55,0.1)' : 'none'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Export CSV */}
            <button
              onClick={() => exportCSV(filteredOrders, vendorName, preset)}
              className="px-5 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-widest transition-all flex items-center gap-2"
              style={{ background: '#E8B84B', color: '#1a0808' }}
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 space-y-8">

        {/* ══════════════════════════════════════════
            B. DIVINE METRIC CARDS
        ══════════════════════════════════════════ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1 */}
          <MetricCard
            icon="inventory_2"
            label="Total Products"
            value={totalProducts}
            unit="listed items"
            accentColor="#E8B84B"
            trend="Active Listings"
            trendColor="#E8B84B"
          />
          {/* Card 2 */}
          <MetricCard
            icon="shopping_bag"
            label="Units Sold"
            value={totalUnits}
            unit="total units ordered"
            accentColor="#22c55e"
            trend={`Across ${totalOrders} orders`}
            trendColor="rgba(255,255,255,0.4)"
          />
          {/* Card 3 */}
          <MetricCard
            icon="account_balance_wallet"
            label="Total Revenue"
            value={`₹${totalRevenue.toLocaleString('en-IN')}`}
            unit="earned from sales"
            accentColor="#a78bfa"
            trend="Lifetime Earnings"
            trendColor="rgba(255,255,255,0.4)"
          />
          {/* Card 4 */}
          <MetricCard
            icon="pending_actions"
            label="Pending Orders"
            value={pendingOrders}
            unit="awaiting fulfilment"
            accentColor="#f87171"
            trend={pendingOrders > 0 ? 'Needs attention' : 'All clear'}
            trendColor={pendingOrders > 0 ? '#f87171' : '#22c55e'}
          />
        </div>

        {/* ══════════════════════════════════════════
            C + D. SALES CHART + ACTIVITY FEED
        ══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Sales Chart */}
          <div className="lg:col-span-3 rounded-3xl p-6 relative overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,175,55,0.15)',
              backdropFilter: 'blur(12px)'
            }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h3 className="font-serif font-bold text-lg" style={{ color: '#FDF6E3' }}>
                  Sales Performance
                </h3>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(212,175,55,0.6)' }}>
                  Top performing {chartToggle === 'product' ? 'products' : 'centres'}
                </p>
              </div>
              {/* Toggle */}
              {showCentreToggle && (
                <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
                  {[['product', 'By Product'], ['centre', 'By Centre']].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setChartToggle(key)}
                      className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest transition-all"
                      style={{
                        background: chartToggle === key ? 'rgba(212,175,55,0.2)' : 'transparent',
                        color: chartToggle === key ? '#E8B84B' : 'rgba(212,175,55,0.4)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Chart */}
            {activeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={activeChartData} margin={{ top: 20, right: 5, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.06)" vertical={false} />
                  <XAxis
                    dataKey="_id"
                    tick={{ fill: 'rgba(212,175,55,0.4)', fontSize: 10, fontWeight: 700 }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(v) => v.length > 15 ? v.substring(0, 15) + '...' : v}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(212,175,55,0.3)', fontSize: 9 }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => {
                      if (v === 0) return '';
                      if (chartToggle === 'product') {
                        if (v >= 1000) return `${(v/1000).toFixed(1)}k`;
                        return `${v}`;
                      }
                      if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
                      if (v >= 1000) return `₹${(v/1000).toFixed(0)}k`;
                      return `₹${v}`;
                    }}
                  />
                  <RechartsTooltip content={<GoldTooltip isProductView={chartToggle === 'product'} />} cursor={{ fill: 'rgba(212,175,55,0.05)' }} />
                  <Bar
                    dataKey={chartToggle === 'product' ? 'count' : 'totalSales'}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={50}
                  >
                    {activeChartData.map((entry, index) => (
                      <Cell key={`cell-`} fill={['#B8893C','#27A87C','#8F5EF2','#D96A88'][index % 4]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[260px] text-center">
                <span className="material-symbols-outlined text-4xl mb-2" style={{ color: 'rgba(212,175,55,0.2)' }}>bar_chart</span>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>No sales data available yet.</p>
              </div>
            )}
          </div>

          {/* ── Live Activity Feed ─────────────────── */}
          <div className="lg:col-span-2 rounded-3xl p-6 relative overflow-hidden flex flex-col"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,175,55,0.15)',
              backdropFilter: 'blur(12px)'
            }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <h3 className="font-serif font-bold text-sm" style={{ color: '#FDF6E3' }}>
                Recent Orders
              </h3>
            </div>

            {feed.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <span className="material-symbols-outlined text-5xl mb-3" style={{ opacity: 0.2 }}>inventory</span>
                <p className="text-xs" style={{ color: 'rgba(212,175,55,0.4)' }}>Awaiting your first orders...</p>
              </div>
            ) : (
              <div ref={feedRef} className="flex-1 space-y-3 overflow-y-auto pr-1" style={{ maxHeight: 320 }}>
                {feed.map((item, i) => {
                  const timeAgo = getTimeAgo(item.time);
                  return (
                    <div
                      key={item.id || i}
                      className="flex items-start gap-3 p-3 rounded-2xl transition-all hover:opacity-90"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: 'rgba(34,197,94,0.12)' }}>
                        <span className="material-symbols-outlined text-[16px]" style={{ color: '#22c55e' }}>
                          receipt_long
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Title: Product Name with bold quantity */}
                        <p className="text-sm font-bold truncate" style={{ color: '#FDF6E3' }}>
                          {item.quantity > 1 && <span className="text-[#E8B84B] mr-1">{item.quantity}x</span>}
                          {item.productName}
                        </p>
                        {/* Subtitle: Buyer Name */}
                        <p className="text-[10px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          Ordered by <span className="font-medium text-white">{item.buyer}</span>
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-green-400">
                              ₹{item.amount.toLocaleString('en-IN')}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[9px] font-bold" 
                                  style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
                              {item.status}
                            </span>
                          </div>
                          <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                            {timeAgo}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => navigate('/orders')}
              className="mt-4 w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all hover:opacity-80"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.2)',
                color: '#E8B84B'
              }}
            >
              View All Orders →
            </button>
          </div>
        </div>

        {/* ── Quick action links ───────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            onClick={() => navigate('/products')}
            className="rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:border-[#E8B84B]/40 transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/10 text-amber-500">
              <span className="material-symbols-outlined">inventory_2</span>
            </div>
            <div>
              <div className="font-bold text-base text-[#FDF6E3]">My Products</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/30 mt-0.5">Manage listings</div>
            </div>
          </div>
          
          <div
            onClick={() => navigate('/orders')}
            className="rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:border-[#E8B84B]/40 transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-green-500/10 text-green-500">
              <span className="material-symbols-outlined">local_shipping</span>
            </div>
            <div>
              <div className="font-bold text-base text-[#FDF6E3]">My Orders</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/30 mt-0.5">Track & Fulfill</div>
            </div>
          </div>

          <div
            onClick={() => navigate('/wallet')}
            className="rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:border-[#E8B84B]/40 transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/10 text-blue-400">
              <span className="material-symbols-outlined">account_balance_wallet</span>
            </div>
            <div>
              <div className="font-bold text-base text-[#FDF6E3]">Wallet</div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/30 mt-0.5">Earnings & Payouts</div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            E. TRANSACTION LEDGER
        ══════════════════════════════════════════ */}
        <div className="mt-8 rounded-3xl overflow-hidden relative"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(212,175,55,0.15)',
            backdropFilter: 'blur(12px)'
          }}>
          <div className="p-6 border-b" style={{ borderColor: 'rgba(212,175,55,0.1)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-bold" style={{ color: '#FDF6E3' }}>Transaction Ledger</h2>
              <p className="text-sm font-bold" style={{ color: 'rgba(212,175,55,0.6)' }}>
                {filteredOrders.length} {filteredOrders.length === 1 ? 'Entry' : 'Entries'}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ background: 'rgba(212,175,55,0.05)' }}>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Date</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Order ID</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Item Details</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Buyer</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Status</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(255,255,255,0.05)]">
                {filteredOrders.length > 0 ? (
                  filteredOrders.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).map(order => (
                    <tr key={order._id} className="hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                      <td className="px-6 py-5">
                        <div className="text-sm font-bold text-[#FDF6E3]">
                          {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </div>
                        <div className="text-[10px] text-white/40 mt-1">
                          {new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="font-mono text-xs text-white/50 bg-white/5 px-2 py-1 rounded">
                          {String(order._id).slice(-8).toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-sm font-bold text-green-400">
                          {order.quantity > 1 && <span className="text-[#E8B84B] mr-1">{order.quantity}x</span>}
                          {order.productName || order.productId?.name || 'Marketplace Item'}
                        </div>
                        <div className="text-[10px] text-white/40 mt-1 flex items-center gap-2">
                          <span className="material-symbols-outlined text-[12px] text-green-500/70">shopping_bag</span>
                          Marketplace Order
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-sm text-white/80">{order.userId?.name || 'Customer'}</div>
                        <div className="text-[10px] text-white/40">{order.userId?.email || ''}</div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider"
                          style={{
                            background: order.status === 'delivered' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
                            color: order.status === 'delivered' ? '#22c55e' : '#eab308'
                          }}>
                          {order.status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="text-sm font-bold text-[#FDF6E3]">₹{fmt(order.totalAmount)}</div>
                        {order.quantity > 1 && (
                           <div className="text-[10px] text-white/30 mt-1">
                             ₹{fmt(order.unitPrice || Math.round(order.totalAmount/order.quantity))} / unit
                           </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-4 text-white/20">
                        <span className="material-symbols-outlined text-3xl">receipt_long</span>
                      </div>
                      <p className="text-white/40 font-bold">No transactions found for this period</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <EditVendorProfileModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
      />
    </div>
  );
};

// ─────────────────────────────────────────
// Metric Card Sub-component
// ─────────────────────────────────────────
const MetricCard = ({ icon, label, value, unit, accentColor, trend, trendColor }) => (
  <div className="rounded-2xl p-5 relative overflow-hidden group hover:scale-[1.02] transition-transform cursor-default"
    style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${accentColor}22`,
      backdropFilter: 'blur(8px)'
    }}>
    {/* Glow blob */}
    <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-10 transition-opacity group-hover:opacity-20"
      style={{ background: accentColor, filter: 'blur(20px)' }} />

    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
      style={{ background: `${accentColor}18` }}>
      <span className="material-symbols-outlined text-[20px]" style={{ color: accentColor }}>{icon}</span>
    </div>

    <div className="font-bold text-2xl leading-none mb-1.5" style={{ color: '#FDF6E3' }}>
      {value}
    </div>
    <div className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: `${accentColor}99` }}>
      {label}
    </div>
    <div className="text-[10px]" style={{ color: trendColor || 'rgba(255,255,255,0.3)' }}>
      {trend}
    </div>
  </div>
);

// ─────────────────────────────────────────
// Time ago helper
// ─────────────────────────────────────────
function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default VendorDashboard;
